import { Injectable, Injector, QueryList, ElementRef, NgZone } from '@angular/core';
import { IJournalService } from "@journal/interfaces/i-journal-service";
import { LayoutState, ViewStateRecordsAlias } from "@common/services/layout-state";
import { DataStore } from "@suite/common/data-models/data-store/data-store";
import { ServiceResponse } from "@common/data-models/service/service-response";
import { AppService } from "@suite/app.service";
import { SearchResults } from "@suite/common/data-models/search-results/search-results";
import { SingleViewStateRecord } from "@common/data-models/records/single-viewstate-record";
import { Subscription } from "rxjs";
import { JournalSearchService } from "@journal/services/journal-search-service";
import { VisualStateService } from "@common/services/visual-state-service";
import { NavigationService } from "@common/services/navigation-service";
import { ObjectTypes } from "@common/enums/object-types";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { ViewStateTypes } from "@common/enums/viewstate-types";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { LanguageSettings } from "@common/services/language-settings";
import { ViewStateOptions } from "@common/data-models/navigation/viewstate-options";
import { ViewStateLayouts } from "@common/enums/viewstate-layouts";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { DraftViewStateRecord } from "@common/data-models/records/draft-viewstate-record";
import { JournalSearchResults } from '@journal/data-models/journal-search-results/journal-search-results';
import { FieldDef } from "@common/data-models/service/field-def";

@Injectable({ providedIn: 'root' })
export class JournalService implements IJournalService {

    private viewStateRecordSubscription: Subscription;
    private viewStateRecordPropertySubscription: Subscription;

    constructor(
        public appService: AppService,
        public journalSearchService: JournalSearchService,
        public viewStateService: LayoutState,
        public visualStateService: VisualStateService,
        public languageSettings: LanguageSettings,
        public globalTablesService: GlobalTablesService,
        public nomWebServicesJson: NomWebServicesJson,
        public ngZone: NgZone,
        public navigationService: NavigationService
    ) {
        JournalSearchResults.journalService = this;
        
        //Clipboard.copyViewToClipboardHandlers["playbook"] = this.copyViewToClipBoard.bind(this);
        //Clipboard.renderers["playbook"] = this.copyToClipboardRenderer.bind(this);

        this.globalTablesService.staticPropertyHandlers["DRAFT"] = this.getDraftStaticProperties.bind(this);

        this.globalTablesService.isPropertyForType[ObjectTypes.DRAFT] = this.isDraftProperty.bind(this);

        //this.globalTablesService.populateDefaultValueCallbacks[ObjectTypes.WIRE] = this.populateDefaultValuesForTopic.bind(this);

        //this.globalTablesService.setFieldDefCallbacks[ObjectTypes.WIRE] = this.setFieldDefsForTopics.bind(this);

        this.globalTablesService.getGroupingOptions[ViewStateTypes.DRAFT] = this.getGroupingOptionsForDrafts.bind(this);
        this.globalTablesService.getGroupingOptions[ViewStateTypes.DRAFTSEARCH] = this.getGroupingOptionsForDrafts.bind(this);

        this.globalTablesService.getSortingOptions[ViewStateTypes.DRAFT] = this.getSortingOptionsForDrafts.bind(this);
        this.globalTablesService.getSortingOptions[ViewStateTypes.DRAFTSEARCH] = this.getSortingOptionsForDrafts.bind(this);

        this.globalTablesService.getLayoutOptions[ViewStateTypes.DRAFT] = this.getLayoutOptionsForDrafts.bind(this);
        this.globalTablesService.getLayoutOptions[ViewStateTypes.DRAFTSEARCH] = this.getLayoutOptionsForDrafts.bind(this);;

        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.DRAFT] = this.getFiltersForDraft(ViewStateTypes.DRAFT).bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.DRAFTSEARCH] = this.getFiltersForDraft(ViewStateTypes.DRAFT).bind(this);
    }

    public isDraftProperty(fieldDef: FieldDef): boolean {
        return fieldDef.DraftProp;
    }

    public getDraftStaticProperties(): { [id: string]: boolean } {
        let ret: { [id: string]: boolean } = {};
        ret[PSTFieldNames.PSTWireCategory] = true;
        ret[PSTFieldNames.PSTWireTitle] = true;
        ret[PSTFieldNames.PSTWireText] = true;
        ret[PSTFieldNames.PSTWireImage] = true;
        ret[PSTFieldNames.PSTWireProvider] = true;
        return ret;
    }

    public getSortingOptionsForDrafts(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.PSTModTime;
        ret.Options = [
            { id: PSTFieldNames.PSTModTime, title: this.languageSettings.PSTWebGroupByDateLabel },
            //{ id: PSTFieldNames.PSTTopicStoryOwner, title: this.languageSettings.PSTWebGroupByOwnerLabel },
            //{ id: PSTFieldNames.PSTTopicSlug, title: this.languageSettings.PSTWebSlugLabel },
            //{ id: PSTFieldNames.PSTTopicSummary, title: this.languageSettings.PSTWebDescriptionLabel },
            //{ id: PSTFieldNames.PSTTopicStatus, title: this.languageSettings.PSTWebGroupByStatusLabel },
            //{ id: PSTFieldNames.PSTTopicDepartment, title: this.languageSettings.PSTWebGroupByDepartmentLabel },
            //{ id: PSTFieldNames.PSTTopicType, title: this.languageSettings.PSTWebTopicTypePlaceholder }
        ];
        return ret;
    }

    private _groupByDrafts: Array<{ id: string, title: string }> = [];
    public get groupByDrafts(): Array<{ id: string, title: string }> {
        if ((this._groupByDrafts.length == 0) && this.languageSettings) {
            this._groupByDrafts = this.getGroupingOptionsForDrafts().Options;
        }
        return this._groupByDrafts;
    }

    private _groupByDraftKeys: Array<string> = [];
    public get groupByDraftKeys(): Array<string> {
        if ((this._groupByDraftKeys.length == 0) && (this.groupByDrafts.length > 0)) {
            this._groupByDraftKeys = this.groupByDrafts.map(item => item.id);
        }
        return this._groupByDraftKeys;
    }



    public getGroupingOptionsForDrafts(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.PSTModTime;
        ret.Options = [
            { id: PSTFieldNames.PSTModTime, title: this.languageSettings.PSTWebGroupByDateLabel }
        ];
        return ret;
    }

    public getLayoutOptionsForDrafts(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = ViewStateLayouts.CARD;
        ret.Options = [
            { id: ViewStateLayouts.CARD, title: this.languageSettings.PSTWebViewCardLabel }

        ];
        return ret;
    }

    public getFiltersForDraft(type: number): (item: FieldDef) => boolean {
        return (item: FieldDef) => {
            if (item.Briefable && (item["DraftProp"] == true)) {
                return true;
            }
            return false;
        }
    }

    public activeCollectionPollerStarted: boolean = false;

    public get journalSearchResults(): JournalSearchResults {
        return DataStore.getSearchResults<JournalSearchResults>("journal");
    }

    public async loadDrafts(vs?: ViewStateRecordsAlias, guid?: string) {
        if (!vs) vs = this.viewStateService.ViewStateRecord;
        if (this.navigationService.currentListView) this.navigationService.currentListView.showLoader();
        await this.appService.ngZone.runOutsideAngular(async () => {
            //this.appService.LoadingTopicsSource.next(true);
            DataStore.disposeSearchResults();
            SearchResults.newlyAddedItems.dispose();

            let res: ServiceResponse<SearchResults> = await this.journalSearchService.getSearchResults(vs, guid);

            if (!this.activeCollectionPollerStarted) {
            this.activeCollectionPollerStarted = true;
            this.startActiveCollectionPoller();
            }

            if (res.status) {
                DataStore.registerSearchResults("journal", res.data);
                this.journalSearchResults.rebuildGroups((<SingleViewStateRecord>vs).viewStateGrouping.Value, (<SingleViewStateRecord>vs).viewStateSorting.Value, (<SingleViewStateRecord>vs).viewStateSortOrder, (<SingleViewStateRecord>vs).viewStateGroupSortOrder);
            } else {
                // Error
            }
            this.appService.LoadingDataSource.next(false);
            this.appService.timeoutQueue.setTimeout(async () => {
                this.appService.ngZone.run(() => {
                    this.navigationService.currentListChangeDetectorRef.detectChanges();
                });
                if (!this.navigationService.CurrentNavigationItem || !this.navigationService.CurrentNavigationItem.isNewView()) {
                    this.navigationService.currentListView.hideLoader();
                }

                this.appService.ngZone.run(() => {
                    this.navigationService.currentListChangeDetectorRef.detectChanges();
                });

                /*if (this.viewStateService.inSlideshowMode) {
                    this.appService.timeoutQueue.setTimeout(() => {
                        this.slideshowService.process();
                    }, 0);
                }*/

                if (this.navigationService.CurrentNavigationItem && this.navigationService.CurrentNavigationItem.isNewView()) {
                    await this.navigationService.saveCurrentNavigationItem();
                    this.navigationService.currentListView.hideLoaderDirectly();
                }
            }, 0);

        });
    }

public startActiveCollectionPoller() {
        this.ngZone.runOutsideAngular(async () => {
            setInterval(async () => {
                if (!this.nomWebServicesJson.noSession) {
                    let vs: ViewStateRecordsAlias = this.viewStateService.ViewStateRecord;
                    if (vs instanceof SingleViewStateRecord) {
                        let res = null;
                        //let res = await this.discoverSearchService.getFilteredResultsGuids(vs);
                        if (res && res.status) {
                            //DataStore.getSearchResults('discover').updateNewGuids(res.data, SearchResults.newlyAddedItems.itemsGuids);
                        } else {
                            this.nomWebServicesJson.noSession = true;
                        }
                    }
                }
            }, this.appService.getSolrPollInvervalInSeconds());
        });
    }
}
